import {
  DeviceOrientationControls,
  Environment,
  OrbitControls,
  Preload,
} from "@react-three/drei";
import { PerspectiveCamera } from "three";
import { useEffect, useRef } from "react";
import { useStore } from "./store/Store";
import { useFrame, useThree } from "@react-three/fiber";
import { use8thWall } from "./hooks/use8thWall";
import { ACTION_TRIGGERS } from "./ActionTriggers";
import { usePhysics } from "./hooks/usePhysics";
import { FxProps, TrackingSettings8thWall } from "./CanvasComponent";

type Props = {
  models: JSX.Element[];
  engine: "8thwall" | "gyro" | "none";
  tracking: TrackingSettings8thWall;
  imageTargets?: Array<string> | null;
  tapToPlace?: boolean;
  physics?: boolean | undefined;
  fxProps?: FxProps;
};

export type Settings8thWall = {
  tapToPlace?: boolean;
};

let firstRender = 0;

export const CanvasContentComponent = ({
  models,
  engine,
  tracking,
  imageTargets,
  physics,
  fxProps,
}: Props) => {
  // FIX: make real time reflections work with bloom
  if (fxProps?.bloom?.enabled) fxProps.realtimeReflections = false;

  const enabled = engine === "8thwall";
  const settings: Settings8thWall = {};
  const triggerAction = useStore((store) => store.triggerAction);
  const addAsset = useStore((store) => store.addAsset);

  const foundTarget = (e: any) => {
    triggerAction(ACTION_TRIGGERS.IMAGE_TARGET_FOUND, "canvas", undefined, e);
  };

  const lostTarget = (e: any) => {
    triggerAction(ACTION_TRIGGERS.IMAGE_TARGET_LOST, "canvas", undefined, e);
  };

  useFrame(() => {
    if (firstRender < 5) {
      firstRender++;
      if (firstRender === 5) {
        triggerAction(ACTION_TRIGGERS.EXPERIENCE_START, "canvas");
      }
    }
  });

  const { scene, camera, gl: renderer } = useThree();

  // useFrame with a 2nd argument can disable the R3F rendering. In this case with a 1 as 2nd argument we disable the R3F rendering.
  // But only in case of 8thWall tracking.
  // We do this because  we have the 8thWall pipelines take care of rendering.
  useFrame(({ gl, scene, camera }) => {
    if (!enabled) {
      gl.render(scene, camera);
    }
  }, 1);

  usePhysics(physics);

  use8thWall({
    enabled,
    tracking,
    camera: camera as PerspectiveCamera,
    scene,
    renderer,
    settings,
    fxProps,
    imageTrackingProps: {
      imageTargets,
      foundTarget,
      lostTarget,
    },
  });

  const trackingGroup = useRef(null);
  useEffect(() => {
    addAsset(trackingGroup, "trackingGroup");
  }, [trackingGroup]);

  return (
    <>
      <ambientLight />
      {engine === "none" && (
        <>
          <OrbitControls />
          <Environment background preset="warehouse" />
        </>
      )}
      {engine === "gyro" && (
        <DeviceOrientationControls></DeviceOrientationControls>
      )}
      <pointLight position={[10, 10, 10]} />
      {/* {!fxProps?.realtimeReflections && (
        <Environment background={false} preset="warehouse" />
      )} */}
      <group name="trackingGroup" ref={trackingGroup}>
        {models}
      </group>
      {/* <axesHelper layers={99}></axesHelper> */}
      <Preload all />
    </>
  );
};
